import type { Me } from '@ft/shared';
import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { upload } from '../lib/download';
import { api } from '../lib/http';
import { Avatar, usePeople, usePhoto } from '../lib/people';
import { Modal } from './ui';

const VIEW = 200; // Vorschau in px
const OUT = 256; // gespeichertes Bild in px

/** Eigenes Profilbild: Bild wählen, rund zuschneiden (Zoom, Verschieben), speichern oder entfernen. */
export function ProfilePhotoDialog({ me, onClose, onDone }: { me: Me; onClose: () => void; onDone: (msg: string) => void }) {
  const { t } = useI18n();
  const { reload } = usePeople();
  const has = !!usePhoto({ id: me.id });
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const canvas = useRef<HTMLCanvasElement>(null);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function pick(f: File | undefined) {
    setErr(null);
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) return setErr(t('photo.err.type'));
    if (f.size > 15 * 1024 * 1024) return setErr(t('photo.err.size'));
    const url = URL.createObjectURL(f);
    const i = new Image();
    i.onload = () => { setImg(i); setZoom(1); setOff({ x: 0, y: 0 }); URL.revokeObjectURL(url); };
    i.onerror = () => { setErr(t('photo.err.type')); URL.revokeObjectURL(url); };
    i.src = url;
  }

  function draw(c: HTMLCanvasElement, size: number) {
    const ctx = c.getContext('2d');
    if (!ctx || !img) return;
    const k = size / VIEW;
    const s = (size / Math.min(img.width, img.height)) * zoom;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, size, size);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, (size - img.width * s) / 2 + off.x * k, (size - img.height * s) / 2 + off.y * k, img.width * s, img.height * s);
  }
  useEffect(() => { if (canvas.current) draw(canvas.current, VIEW); });

  async function save() {
    if (!img) return;
    setBusy(true); setErr(null);
    try {
      const c = document.createElement('canvas');
      c.width = OUT; c.height = OUT;
      draw(c, OUT);
      const blob = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/jpeg', 0.88));
      if (!blob) throw new Error(t('photo.err.type'));
      await upload('PUT', '/api/me/photo', new File([blob], 'profilbild.jpg', { type: 'image/jpeg' }));
      reload();
      onDone(t('photo.saved'));
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  }
  async function remove() {
    setBusy(true); setErr(null);
    try { await api('DELETE', '/api/me/photo'); reload(); onDone(t('photo.removed')); } catch (e) { setErr((e as Error).message); setBusy(false); }
  }

  return (
    <Modal title={t('photo.title')} onClose={() => !busy && onClose()} width={560}
      footer={<>
        {err && <p className="err" role="alert">{err}</p>}
        {has && <button className="btn ghost danger" onClick={remove} disabled={busy}>{t('photo.remove')}</button>}
        <span className="spacer" />
        <button className="btn" onClick={onClose} disabled={busy}>{t('common.cancel')}</button>
        <button className="btn primary" onClick={save} disabled={busy || !img}>{busy ? t('common.saving') : t('common.save')}</button>
      </>}>
      <div className="phdlg">
        <div>
          {img ? (
            <div className="phcrop"
              onPointerDown={(e) => { (e.target as HTMLElement).setPointerCapture(e.pointerId); drag.current = { x: e.clientX, y: e.clientY, ox: off.x, oy: off.y }; }}
              onPointerMove={(e) => { const d = drag.current; if (d) setOff({ x: d.ox + e.clientX - d.x, y: d.oy + e.clientY - d.y }); }}
              onPointerUp={() => { drag.current = null; }}>
              <canvas ref={canvas} width={VIEW} height={VIEW} aria-label={t('photo.preview')} />
              <i />
            </div>
          ) : (
            <div className="phcrop empty"><Avatar id={me.id} name={me.name} initials={me.initials} size={150} /></div>
          )}
          {img && <>
            <label className="small muted" htmlFor="ph-z" style={{ display: 'block', marginTop: 10 }}>{t('photo.zoom')}</label>
            <input id="ph-z" type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} style={{ width: '100%' }} />
            <div className="small muted">{t('photo.dragHint')}</div>
          </>}
        </div>
        <div>
          <p className="muted small" style={{ marginTop: 0 }}>{t('photo.lead')}</p>
          <div className={`phdrop${over ? ' over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files[0]); }}>
            {t('photo.drop')} <button type="button" className="linkbtn" onClick={() => fileRef.current?.click()}>{t('photo.choose')}</button>
            <div className="small muted">{t('photo.formats')}</div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden aria-label={t('photo.choose')} onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
          </div>
          <div className="phsizes" aria-hidden="true">
            {([[20, 'photo.size.list'], [28, 'photo.size.history'], [34, 'photo.size.head'], [56, 'photo.size.start']] as const).map(([s, k]) => (
              <div key={k}>{img ? <PreviewMini src={canvas.current} size={s} dep={`${zoom}|${off.x}|${off.y}`} /> : <Avatar id={me.id} name={me.name} initials={me.initials} size={s} />}<span>{t(k)}</span></div>
            ))}
          </div>
          <p className="small muted">{t('photo.privacy')}</p>
        </div>
      </div>
    </Modal>
  );
}

/** kleine Kopie der Vorschau in verschiedenen Größen */
function PreviewMini({ src, size, dep }: { src: HTMLCanvasElement | null; size: number; dep: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const c = ref.current; const ctx = c?.getContext('2d');
      if (c && ctx && src) { ctx.clearRect(0, 0, c.width, c.height); ctx.drawImage(src, 0, 0, c.width, c.height); }
    });
    return () => cancelAnimationFrame(id);
  });
  return <canvas ref={ref} width={size * 2} height={size * 2} className="avimg" style={{ width: size, height: size }} data-dep={dep} />;
}
